"use client"

import { useMemo, useState } from "react"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Skeleton } from "@/components/ui/skeleton"
import { ColumnFilter } from "@/components/ui/ColumnFilter"
import { AddToBacktestButton } from "@/components/watchlist/AddToBacktestButton"
import { cn, openStockChart } from "@/lib/utils"
import type { EnrichedConditionStock } from "@/types/condition"

interface Props {
  stocks: EnrichedConditionStock[]
  isLoading: boolean
  watchlistCodes?: Set<string>
}

const NULL_LABEL = "(없음)"

// changeSign: "1"=상한가, "2"=상승, "3"=보합, "4"=하한가, "5"=하락
function getChangeColor(sign: string) {
  if (sign === "1" || sign === "2") return "text-red-400"
  if (sign === "4" || sign === "5") return "text-blue-400"
  return "text-zinc-400"
}

function getChangePrefix(sign: string) {
  if (sign === "1") return "↑"
  if (sign === "2") return "▲"
  if (sign === "4") return "↓"
  if (sign === "5") return "▽"
  return ""
}

function fmtNum(s: string) {
  const n = parseInt(s.replace(/[^0-9]/g, ""), 10)
  return isNaN(n) ? s : n.toLocaleString()
}

function SkeletonRows() {
  return (
    <>
      {Array.from({ length: 5 }).map((_, i) => (
        <TableRow key={i} className="border-zinc-800">
          {Array.from({ length: 12 }).map((_, j) => (
            <TableCell key={j}>
              <Skeleton className="h-4 w-full bg-zinc-800" />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  )
}

function MobileSkeletonCards() {
  return (
    <>
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="flex flex-col gap-2 px-4 py-3">
          <Skeleton className="h-4 w-2/3 bg-zinc-800" />
          <Skeleton className="h-3 w-1/3 bg-zinc-800" />
        </div>
      ))}
    </>
  )
}

type ColKey = "market" | "sector"

export function ConditionStockTable({ stocks, isLoading, watchlistCodes }: Props) {
  const [colFilters, setColFilters] = useState<Record<ColKey, Set<string>>>({
    market: new Set(),
    sector: new Set(),
  })

  const updateFilter = (key: ColKey, val: Set<string>) =>
    setColFilters((prev) => ({ ...prev, [key]: val }))

  const marketValues = useMemo(
    () => [...new Set(stocks.map((s) => s.market ?? NULL_LABEL))].filter(Boolean).sort(),
    [stocks]
  )
  const sectorValues = useMemo(
    () => [...new Set(stocks.map((s) => s.sector ?? NULL_LABEL))].sort(),
    [stocks]
  )

  const displayed = useMemo(() => {
    return stocks.filter((s) => {
      if (colFilters.market.size > 0 && !colFilters.market.has(s.market ?? NULL_LABEL)) return false
      if (colFilters.sector.size > 0 && !colFilters.sector.has(s.sector ?? NULL_LABEL)) return false
      return true
    })
  }, [stocks, colFilters])

  return (
    <>
      {/* md 이상: 기존 테이블 */}
      <div className="hidden md:block">
        <Table>
          <TableHeader>
            <TableRow className="border-zinc-800 hover:bg-transparent">
              <TableHead className="w-16 text-zinc-400">
                <div className="flex items-center gap-1">
                  시장
                  <ColumnFilter
                    values={marketValues}
                    selected={colFilters.market}
                    onChange={(v) => updateFilter("market", v)}
                  />
                </div>
              </TableHead>
              <TableHead className="w-28 text-zinc-400">
                <div className="flex items-center gap-1">
                  섹터
                  <ColumnFilter
                    values={sectorValues}
                    selected={colFilters.sector}
                    onChange={(v) => updateFilter("sector", v)}
                  />
                </div>
              </TableHead>
              <TableHead className="w-24 text-zinc-400">종목코드</TableHead>
              <TableHead className="text-zinc-400">종목명</TableHead>
              <TableHead className="w-28 text-right text-zinc-400">현재가</TableHead>
              <TableHead className="w-32 text-right text-zinc-400">전일대비</TableHead>
              <TableHead className="w-24 text-right text-zinc-400">등락율</TableHead>
              <TableHead className="w-28 text-right text-zinc-400">거래량</TableHead>
              <TableHead className="w-24 text-right text-zinc-400">시가</TableHead>
              <TableHead className="w-24 text-right text-zinc-400">고가</TableHead>
              <TableHead className="w-24 text-right text-zinc-400">저가</TableHead>
              <TableHead className="w-10 text-zinc-400" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <SkeletonRows />
            ) : displayed.length === 0 ? (
              <TableRow className="border-zinc-800">
                <TableCell colSpan={12} className="py-12 text-center text-zinc-500">
                  결과가 없습니다.
                </TableCell>
              </TableRow>
            ) : (
              displayed.map((s) => {
                const color = getChangeColor(s.changeSign)
                const prefix = getChangePrefix(s.changeSign)
                return (
                  <TableRow
                    key={s.stockCode}
                    className="border-zinc-800 text-zinc-200 cursor-pointer hover:bg-zinc-800/60"
                    onClick={() => openStockChart(s.stockCode)}
                  >
                    <TableCell className="text-xs text-zinc-400">{s.market ?? "-"}</TableCell>
                    <TableCell className="max-w-[112px] truncate text-xs text-zinc-400">
                      {s.sector ?? "-"}
                    </TableCell>
                    <TableCell className="font-mono text-zinc-400">{s.stockCode}</TableCell>
                    <TableCell className="font-medium">{s.stockName}</TableCell>
                    <TableCell className={cn("text-right font-mono font-semibold", color)}>
                      {fmtNum(s.currentPrice)}
                    </TableCell>
                    <TableCell className={cn("text-right font-mono text-sm", color)}>
                      {prefix} {fmtNum(s.change)}
                    </TableCell>
                    <TableCell className={cn("text-right font-mono text-sm", color)}>
                      {s.changeRate}%
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm text-zinc-300">
                      {fmtNum(s.volume)}
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm">{fmtNum(s.open)}</TableCell>
                    <TableCell className="text-right font-mono text-sm">{fmtNum(s.high)}</TableCell>
                    <TableCell className="text-right font-mono text-sm">{fmtNum(s.low)}</TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <AddToBacktestButton
                        stockCode={s.stockCode}
                        inWatchlist={watchlistCodes?.has(s.stockCode) ?? false}
                      />
                    </TableCell>
                  </TableRow>
                )
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* md 미만: 카드형 리스트 */}
      <div className="divide-y divide-zinc-800 md:hidden">
        {isLoading ? (
          <MobileSkeletonCards />
        ) : displayed.length === 0 ? (
          <div className="py-12 text-center text-zinc-500">결과가 없습니다.</div>
        ) : (
          displayed.map((s) => {
            const color = getChangeColor(s.changeSign)
            const prefix = getChangePrefix(s.changeSign)
            return (
              <div
                key={s.stockCode}
                className="flex min-h-[44px] cursor-pointer items-center justify-between gap-3 px-4 py-3 text-zinc-200 active:opacity-80"
                onClick={() => openStockChart(s.stockCode)}
              >
                <div className="flex min-w-0 flex-col gap-0.5">
                  <span className="truncate font-medium">{s.stockName}</span>
                  <span className="font-mono text-xs text-zinc-500">
                    {s.stockCode}
                    {s.market ? ` · ${s.market}` : ""}
                    {s.sector ? ` · ${s.sector}` : ""}
                  </span>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <div className="flex flex-col items-end gap-0.5">
                    <span className={cn("font-mono text-sm font-semibold", color)}>
                      {fmtNum(s.currentPrice)}
                    </span>
                    <span className={cn("font-mono text-xs", color)}>
                      {prefix} {s.changeRate}%
                    </span>
                  </div>
                  <AddToBacktestButton
                    stockCode={s.stockCode}
                    inWatchlist={watchlistCodes?.has(s.stockCode) ?? false}
                  />
                </div>
              </div>
            )
          })
        )}
      </div>
    </>
  )
}
